import React, { useEffect, useRef, useState } from "react";
import { apiGet } from "../../utils/apiFetch";
import apiPath from "../../utils/pathObj";
import { FaVideo} from "react-icons/fa";

const ReportView = ({ sessionId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [showRecording, setShowRecording] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;

    const fetchReport = async () => {
      try {
        const res = await apiGet(`${apiPath.aiInterviewReport}/${sessionId}`, {});
        if (res?.data?.success) setData(res.data.results);
      } catch (err) {
        console.error("Failed to load report:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [sessionId]);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      if (audioRef.current) audioRef.current.src = "";
    };
  }, []);

  const toggleAudio = async () => {
    const url = data?.session?.recordingUrls?.audio;
    if (!url) return;

    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(url);
        audioRef.current.onended = () => setAudioPlaying(false);
        audioRef.current.onerror = () => setAudioPlaying(false);
      }

      if (audioPlaying) {
        audioRef.current.pause();
        setAudioPlaying(false);
      } else {
        await audioRef.current.play();
        setAudioPlaying(true);
      }
    } catch (err) {
      console.error("Audio error:", err);
      setAudioPlaying(false);
    }
  };

  if (loading)
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white p-8 rounded-lg">Loading report...</div>
      </div>
    );

  if (!data)
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white p-8 rounded-lg">No report found.</div>
      </div>
    );

  const { session, transcript = [], report, facialInsights } = data;
  const audioUrl = session?.recordingUrls?.audio;
  const videoUrl = session?.recordingUrls?.video;
  const hasRecording = audioUrl || videoUrl;

  // ✅ FIXED: Get the facial data from the live property
  const facialData = facialInsights?.live || facialInsights?.batch || null;

  // ✅ FIXED: Safely access perQuestion data
  const getFaceData = (index) => {
    if (facialData?.perQuestion && Array.isArray(facialData.perQuestion)) {
      return facialData.perQuestion[index] || null;
    }
    return null;
  };

  const ScoreCard = ({ title, score, bg }) => (
    <div className={`p-3 ${bg} rounded-lg`}>
      <div className="text-xs text-gray-500">{title}</div>
      <div className="text-xl font-bold">{score ?? "N/A"}/10</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 relative">

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl"
        >
          ×
        </button>

        <h2 className="text-2xl font-bold mb-4">Interview Report</h2>

        {/* Scores */}
        {report && (
          <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <ScoreCard title="Technical" score={report.technical} bg="bg-blue-50" />
            <ScoreCard title="Communication" score={report.communication} bg="bg-green-50" />
            <ScoreCard title="Problem Solving" score={report.problemSolving} bg="bg-yellow-50" />
            <ScoreCard title="Overall" score={report.overallScore} bg="bg-purple-50" />
          </div>
        )}

        {/* Recommendation */}
        {report?.recommendation && (
          <div className="mb-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <strong>Recommendation:</strong>{" "}
            <span
              className={`font-bold uppercase ${
                report.recommendation === "reject"
                  ? "text-red-600"
                  : "text-green-600"
              }`}
            >
              {report.recommendation.replaceAll("_", " ")}
            </span>
          </div>
        )}

        {/* Summary */}
        {report?.summary && (
          <div className="mb-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <strong>Summary:</strong>
            <p className="mt-1 text-gray-700">{report.summary}</p>
          </div>
        )}

        {/* Facial Analytics */}
        {facialData && (
          <div className="mb-6 border-t pt-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <span className="text-blue-600">Video Analytics</span>
              <span className="text-xs font-normal text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                Live Analysis
              </span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
              <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                <div className="text-xs text-gray-500">Expression</div>
                <div className="text-lg font-bold capitalize text-indigo-700">
                  {facialData.dominantExpression || "N/A"}
                </div>
              </div>

              <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                <div className="text-xs text-gray-500">Attentiveness Ratio</div>
                <div className="text-lg font-bold text-purple-700">
                  {Math.round((facialData.attentivenessRatio || 0) * 100)}%
                </div>
              </div>

              <div className="p-3 bg-teal-50 rounded-lg border border-teal-100">
                <div className="text-xs text-gray-500">Engagement Score</div>
                <div className="text-lg font-bold text-teal-700">
                  {Math.round((facialData.engagementScore || 0) * 100)}%
                </div>
              </div>
            </div>

            <div className="text-xs text-gray-400">
              Based on {facialData.sampleCount || 0} video frames analyzed.
            </div>
          </div>
        )}

        {/* Transcript */}
        <div className="mb-6">
          <h3 className="font-semibold mb-2">
            Transcript & Facial Analysis
          </h3>

          <div className="space-y-4 max-h-60 overflow-y-auto border rounded-lg p-4 bg-gray-50">
            {transcript.map((item, idx) => {
              const faceData = getFaceData(idx);

              return (
                <div
                  key={idx}
                  className="border-b border-gray-200 pb-3 last:border-0"
                >
                  <div className="text-sm font-medium text-blue-600">
                    Q{idx + 1} ({item.skill || "General"}):
                  </div>

                  <div className="text-sm text-gray-800 mb-1">
                    {item.question}
                  </div>

                  <div className="text-sm font-medium text-green-600">
                    A:
                  </div>

                  <div className="text-sm text-gray-800 mb-1">
                    {item.answer || "No answer"}
                  </div>

                  {item.score !== null && item.score !== undefined && (
                    <div className="text-xs text-gray-500">
                      Score: {item.score}/10
                    </div>
                  )}

                  {item.feedback && (
                    <div className="text-xs text-gray-500 italic">
                      Feedback: {item.feedback}
                    </div>
                  )}

                  {faceData && (
                    <div className="mt-2 flex flex-wrap gap-3 text-xs bg-white p-2 rounded border border-gray-100">
                      <span className="text-gray-500">
                        Expression:{" "}
                        <b className="text-gray-800 capitalize">
                          {faceData.dominantExpression || "N/A"}
                        </b>
                      </span>

                      <span className="text-gray-500">
                        Face Detected:{" "}
                        <b className="text-gray-800">
                          {Math.round(
                            (faceData.faceDetectedRatio || 0) * 100
                          )}
                          %
                        </b>
                      </span>

                      <span className="text-gray-500">
                        Samples:{" "}
                        <b className="text-gray-800">
                          {faceData.sampleCount || 0}
                        </b>
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* =====================================================
            RECORDING - AT THE END
        ====================================================== */}
        {hasRecording && (
          <div className="mb-4 border-t pt-4">
            <button
              onClick={() => setShowRecording(!showRecording)}
              className="w-full flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100"
            >
              <div className="flex items-center gap-2">
                <FaVideo className="text-blue-600" />

                <div className="text-left">
                  <div className="text-sm font-semibold text-gray-700">
                    Interview Recording
                  </div>

                  <div className="text-xs text-gray-400">
                    {showRecording
                      ? "Hide recording"
                      : "View audio and video recording"}
                  </div>
                </div>
              </div>

              <span className="text-sm text-blue-600 font-medium">
                {showRecording ? "Hide Recording" : "View Recording"}
              </span>
            </button>

            {showRecording && (
              <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                {/* Video */}
                {videoUrl && (
                  <div>
                    <video
                      src={videoUrl}
                      controls
                      preload="metadata"
                      className="w-full max-h-[400px] rounded-lg bg-black"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Close */}
        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportView;